import {isEmpty} from 'ramda'

import {getCookie} from '../../_web/utils/cookies'
import {logger} from '../../utils/logging/logger'
import {ProviderRedirectParams} from '../modules/actions'
import {AnalyticsSDKType, IntegrationsType} from '../types/AnalyticsSDK'
import {ExperimentContext, TrackEventProperties} from '../types/Events'

// The SDK uses browser globals so only load it client side
let segmentAnalytics
if (typeof window !== 'undefined') {
  segmentAnalytics = require('@segment/analytics.js')
}

const analytics = segmentAnalytics as AnalyticsSDKType

export type MetaValue = (string & Record<string, unknown>) | null

const normalizeNullValues = function <
  Metadata extends
    | TrackEventProperties
    | ExperimentContext
    | ProviderRedirectParams
>(metadata?: Metadata) {
  if (metadata == undefined) metadata = {} as Metadata

  return Object.keys(metadata).reduce(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function (mem: Record<string, any>, k) {
      let v = (metadata as Metadata)[
        k as keyof (TrackEventProperties | ExperimentContext)
      ] as MetaValue
      if (
        (typeof v === 'string' && v === '') ||
        (typeof v === 'object' && isEmpty(v)) ||
        v === undefined
      ) {
        v = null
      }

      mem[k] = v
      return mem
    },
    {} as TrackEventProperties | ExperimentContext
  )
}

type SegmentDestinationSettings = {apiHost?: string; apiKey?: string}

type AnalyticsOptions = Record<string, unknown>

/**
 * Points the SDK's destination at the shared EvAS stream for the duration of one call, then puts
 * back whatever was live.
 *
 * Segment.io sends synchronously from its `invoke` listener, so the swap, the call and the restore
 * fit in one tick. An unset env pair or an uninitialized SDK sends on the live endpoint rather
 * than dropping the call.
 */
const sendOnSharedStream = <T>(send: () => T): T => {
  const apiHost = process.env.NEXT_PUBLIC_ANALYTICS_SHARED_ENDPOINT
  const apiKey = process.env.NEXT_PUBLIC_ANALYTICS_SHARED_API_KEY

  if (!apiHost || !apiKey || !analytics?.initialized) return send()

  const previous = AnalyticsUtils.getSegmentDestinationSettings()

  try {
    AnalyticsUtils.overrideSegmentDestinationSettings(apiHost, apiKey)
    return send()
  } finally {
    AnalyticsUtils.overrideSegmentDestinationSettings(
      previous.apiHost,
      previous.apiKey
    )
  }
}

/**
 * Analytics helper functions object
 */
const AnalyticsUtils = {
  /** Default for log events */
  logEvents: false,
  /**
   * Set the log events default status
   * @param status - whether to log events
   */
  setLogEventsStatus: (status: boolean) => {
    AnalyticsUtils.logEvents = status
  },
  /** Return if analytics is initialized */
  analyticsPlatformEnabled() {
    return Boolean(analytics?.initialized)
  },
  /**
   * Initialize analytics once. Repeat calls would leak another integration that
   * keeps posting to the baseline endpoint.
   * @param integrations - configuration for integrations
   * @param anonymousId
   */
  initialize(integrations: IntegrationsType, anonymousId: string) {
    if (analytics?.initialized) return

    analytics?.initialize?.(integrations)
    analytics?.user?.().anonymousId(anonymousId)
  },
  /**
   * Overrides the destination options for the Segment / EvAS API endpoint.
   * Used for sending events to a different endpoint, for instance while migrating to a new endpoint.
   * @remarks This method modifies internals of the Analytics.js implementation as there is no official API available.
   * @param apiHost - URL of the analytics endpoint (excluding https://)
   * @param apiKey - API key for the analytics endpoint
   */
  overrideSegmentDestinationSettings(apiHost?: string, apiKey?: string) {
    // @ts-expect-error: Property '_integrations' does not exist on type 'AnalyticsSDKType'.
    analytics._integrations['Segment.io'].options.apiHost = apiHost
    // @ts-expect-error: Property '_integrations' does not exist on type 'AnalyticsSDKType'.
    analytics._integrations['Segment.io'].options.apiKey = apiKey
  },

  /**
   * Reads back the destination options currently set for the Segment / EvAS API endpoint.
   * Lets a caller restore whatever was in place rather than guessing at a default.
   * @remarks Same caveat as {@link AnalyticsUtils.overrideSegmentDestinationSettings} — this reads
   * Analytics.js internals because no official API exposes them.
   */
  getSegmentDestinationSettings(): SegmentDestinationSettings {
    // @ts-expect-error: Property '_integrations' does not exist on type 'AnalyticsSDKType'.
    const {apiHost, apiKey} = analytics._integrations['Segment.io'].options

    return {apiHost, apiKey}
  },

  /**
   * Track events
   * @param event - event name
   * @param metadata - extra event data
   */
  trackEvent(
    event: string,
    metadata?: TrackEventProperties | ExperimentContext | ProviderRedirectParams
  ) {
    const normalizedMetadata = normalizeNullValues(metadata) as
      | TrackEventProperties
      | ExperimentContext

    if (AnalyticsUtils.logEvents) {
      logger.info(`[TRACK] ${event}`, normalizedMetadata)
    }

    return analytics?.track?.(event, normalizedMetadata)
  },

  /**
   * Used to link users and their actions to a recognizable `userId` and `traits` [Segment Docs](https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#identify)
   * @param userId - The user ID of a signed-in user. Do not pass an anonymousId as userId, since the user will incorrectly be considered to be signed in.
   * @param traits - A dictionary of traits you know about the user, like their email or name. `fbp` and `fbc` Meta pixel cookies are automatically merged from document cookies when present; any explicitly passed values take precedence.
   * @param options - A dictionary of options. For example, enable or disable specific destinations for the call. Note: If you do not pass a `traits` object, pass an empty object (as an '{}') before `options`.
   * @param callback - A function executed after a timeout of 300 ms, giving the browser time to make outbound requests first.
   */
  identify(
    userId: Parameters<AnalyticsSDKType['identify']>[0],
    traits?: Parameters<AnalyticsSDKType['identify']>[1],
    options?: Parameters<AnalyticsSDKType['identify']>[2],
    callback?: Parameters<AnalyticsSDKType['identify']>[3]
  ) {
    const enrichedTraits = {
      fbp: getCookie('_fbp'),
      fbc: getCookie('_fbc'),
      ...traits
    }
    return sendOnSharedStream(() =>
      analytics?.identify?.(userId, enrichedTraits, options, callback)
    )
  },

  /**
   * Track a page view.
   * @param name - name of the page
   * @param properties - extra page data
   */
  trackPageView(name?: string, properties?: AnalyticsOptions) {
    sendOnSharedStream(() => analytics?.page?.('Page', name, properties))
  },

  user: analytics?.user,

  /**
   * Segment persist userId and anonymousId. We need to clean up the userId after the user logout or change,
   * but we need to keep the anonymousId as it is. Calling `analytics.reset` will also update anonymousId,
   * which is not what we want. This function will reset the userId and keep the anonymousId value.
   * @param userId
   */
  resetAnalytics: (userId?: string) => {
    const anonymousId = analytics.user().anonymousId()
    const analyticsUserId = analytics.user().id()
    if (analyticsUserId && userId !== analyticsUserId) {
      analytics.reset()
      analytics.user().anonymousId(anonymousId)
    }
  }
}

export default AnalyticsUtils

export {isGoodBot} from './botDetection'
